import React, { useEffect, useMemo, useState } from 'react';
import { getFuelTankList } from '../../../../../slices/thunks';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';
import { ECHARTS_COLOR_PALETTE } from '../../../../../Components/Hooks/useColorArrayHooks';

const prepareChartDataECharts = data => {
	if (!Array.isArray(data))
		return { series: [], categories: [], fuelSummary: [] };

	const tanks = data.map(item => ({
		name: item.name || 'Sin Nombre',
		fuelType: item.fuel_type?.name || 'Desconocido',
		unit: item.unit_measurement?.code || 'N/A',
		barge: item.feeding_barges?.name || null,
		stock: item.stock || 0,
		capacity: item.capacity || 0,
		available: Math.max(0, (item.capacity || 0) - (item.stock || 0)),
	}));

	const categories = tanks.map(tank => tank.name);

	const series = [
		{
			name: 'Saldo',
			type: 'bar',
			stack: 'total',
			colorBy: 'data',
			emphasis: {
				focus: 'series',
			},
			data: tanks.map(tank => ({
				value: tank.stock,
				extra: {
					barge: tank.barge,
					fuelType: tank.fuelType,
					tankName: tank.name,
					stock: tank.stock,
					capacity: tank.capacity,
					unit: tank.unit,
				},
			})),
		},
		{
			name: 'Disponible',
			type: 'bar',
			stack: 'total',
			emphasis: {
				focus: 'series',
			},
			data: tanks.map(tank => ({
				value: tank.available,
				itemStyle: {
					color: '#e0e0e0',
				},
				extra: {
					barge: tank.barge,
					fuelType: tank.fuelType,
					tankName: tank.name,
					stock: tank.stock,
					capacity: tank.capacity,
					unit: tank.unit,
				},
			})),
		},
	];

	return {
		series,
		categories,
		fuelSummary: Object.values(
			tanks.reduce((acc, tank) => {
				if (!acc[tank.fuelType])
					acc[tank.fuelType] = {
						fuelType: tank.fuelType,
						totalStock: 0,
						unit: tank.unit,
					};
				acc[tank.fuelType].totalStock += tank.stock;
				return acc;
			}, {})
		),
	};
};

const FuelTankInfoEChart = ({
	establishmentId,
	title,
	height = '100%',
	um = '',
	legendPosition = 'bottom',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();

	const buildMultiColorLegendIcon = colors => {
		const width = 40;
		const height = 12;
		const used = (
			colors && colors.length ? colors : ECHARTS_COLOR_PALETTE
		).slice(0, 6);
		const segW = width / used.length;
		const rects = used
			.map(
				(c, i) =>
					`<rect x="${
						i * segW
					}" y="0" width="${segW}" height="${height}" fill="${c}"/>`
			)
			.join('');
		const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">${rects}</svg>`;
		return `image://data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
	};

	const buildSingleColorRectIcon = color => {
		const width = 40;
		const height = 12;
		const svg = `<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"${width}\" height=\"${height}\"><rect x=\"0\" y=\"0\" width=\"${width}\" height=\"${height}\" fill=\"${color}\"/></svg>`;
		return `image://data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
	};

	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		data => ({
			data: data.fuelTankList,
			loading_tank: data.isLoadingList,
			error: data.error,
		})
	);

	const { data, loading_tank, error } = useSelector(selectorFuelTank);

	useEffect(() => {
		if (establishmentId) {
			dispatch(getFuelTankList({ establishment_id: establishmentId }));
		}
	}, [establishmentId]);

	// no local loaded-once tracking; leverage data presence for initial spinner behavior

	const { series, categories, fuelSummary } = useMemo(() => {
		if (data.length > 0) {
			return prepareChartDataECharts(data);
		}
		return { series: [], categories: [], fuelSummary: [] };
	}, [data]);

	const usedSaldoColors =
		categories && categories.length
			? ECHARTS_COLOR_PALETTE.slice(
					0,
					Math.min(categories.length, ECHARTS_COLOR_PALETTE.length)
			  )
			: ECHARTS_COLOR_PALETTE.slice(0, 2);
	const saldoLegendIcon = buildMultiColorLegendIcon(usedSaldoColors);
	const disponibleLegendIcon = buildSingleColorRectIcon('#e0e0e0');

	const options = {
		color: ECHARTS_COLOR_PALETTE,
		tooltip: {
			trigger: 'axis',
			axisPointer: { type: 'shadow' },
			formatter: function (params) {
				if (!params || !params.length) return '';
				const point = params[0];
				const { name } = point;
				const saldoParam = params.find(p => p.seriesName === 'Saldo');
				const disponibleParam = params.find(
					p => p.seriesName === 'Disponible'
				);
				const saldo = saldoParam?.data;
				const disponible = disponibleParam?.data;
				const extra = saldo?.extra || disponible?.extra || {};
				const assignedColor =
					saldoParam?.color || disponibleParam?.color || '#666';
				return `
					<div style="border-radius: 8px; padding: 10px; font-size: 11px; text-align: left; min-width: 100px; background: #fff; box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);">
					  <strong>${name}</strong><br/>
					  Tipo: <span style="color:${assignedColor}">${extra.fuelType || ''}</span><br/>
					  Capacidad: <strong>${extra?.capacity ?? ''} ${extra.unit || ''}</strong><br/>
					  Saldo: <strong>${saldo?.value ?? 0} ${extra.unit || ''}</strong><br/>
					  Disponible: <strong>${disponible?.value ?? 0} ${extra.unit || ''}</strong>
					</div>
				`;
			},
		},
		grid: {
			top: '6%',
			left: '4%',
			right: '8%',
			bottom: '15%',
			containLabel: true,
		},
		legend: {
			data: [
				{ name: 'Saldo', icon: saldoLegendIcon },
				{ name: 'Disponible', icon: disponibleLegendIcon },
			],
			orient: 'horizontal',
			bottom: 10,
			type: 'scroll',
		},
		// Horizontal bar: value axis on X, categories on Y
		xAxis: [
			{
				type: 'value',
				name: um,
				splitLine: { show: true },
			},
		],
		yAxis: [
			{
				type: 'category',
				data: categories,
				splitLine: { show: false },
				axisLabel: {
					interval: 0,
					rotate: 90,
					width: 90,
					overflow: 'truncate',
					ellipsis: '...',
					hideOverlap: true,
					align: 'center',
				},
			},
		],
		series: series,
	};

	const noData = !data?.length;
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

	useEffect(() => {
		if (Array.isArray(data)) {
			setHasLoadedOnce(true);
		}
	}, [data]);

	return (
		<OChartCard
			title={title}
			isLoading={loading_tank && !hasLoadedOnce}
			error={error}
			isEmpty={noData}
			emptyIconClass='ri-gas-station-fill'
			emptyMessage='No cuenta con estanque de combustible'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default FuelTankInfoEChart;
